import React from 'react';
import {
  Calendar,
  Clock,
  Timer,
  Watch,
  Briefcase,
  Globe,
  Ruler,
  Dice1 as Dice,
  Percent,
  Heart,
  ListChecks,
  Key,
  Type,
  FileText,
  Calculator as Calc,
  Palette,
  QrCode,
  AlignLeft,
  FileJson,
  FileText as FilePdf,
  StickyNote,
  Scissors,
  Star,
  Cloud,
  Quote,
  MessageSquare,
  Target,
  ArrowRightLeft,
  ImageIcon,
  Scale,
  Landmark,
  Edit,
  Binary,
  CaseSensitive,
  CreditCard,
  Fuel,
  Code,
  Image as PlaceholderIcon,
  Radio,
  Timer as PomodoroIcon,
  Users,
  ListMinus,
  Repeat,
  SortAsc,
  HandCoins,
  CalendarClock,
  Accessibility, // Added Accessibility icon import
  Link as LinkIcon, // Added LinkIcon import
  Hash, // Added Hash icon import
  FileCode, // Added FileCode icon import
  Table, // Added Table icon import
  TrendingUp, // Added TrendingUp icon import
  ShieldCheck, // Added ShieldCheck icon import
  Receipt, // Added Receipt icon import
  Network, // Added Network icon import
  Brush, // Added Brush icon import
  BookOpenCheck, // Added BookOpenCheck icon import
  Wand2, // Added Wand2 icon import
  Zap // Added Zap icon import
} from 'lucide-react';
import { FaHatCowboy } from 'react-icons/fa';

// Define types for categories and tools
export type ToolCategoryKey = 'productivity' | 'calculation' | 'time' | 'document' | 'creative' | 'utility' | 'fun' | 'wellness';

export interface CategoryStyle {
  from: string;
  to: string;
  shadow: string;
}

export interface Tool {
  path: string;
  icon: React.ElementType; // Use ElementType for component types
  label: string;
  category: ToolCategoryKey;
  description: string;
}

// Tool categories with their gradient colors, explicitly typed
export const toolCategories: Record<ToolCategoryKey, CategoryStyle> = {
  productivity: { from: 'from-blue-400', to: 'to-blue-600', shadow: 'shadow-blue-500/20' },
  calculation: { from: 'from-emerald-400', to: 'to-emerald-600', shadow: 'shadow-emerald-500/20' },
  time: { from: 'from-amber-400', to: 'to-orange-600', shadow: 'shadow-orange-500/20' },
  document: { from: 'from-purple-400', to: 'to-purple-600', shadow: 'shadow-purple-500/20' },
  creative: { from: 'from-pink-400', to: 'to-rose-600', shadow: 'shadow-rose-500/20' },
  utility: { from: 'from-cyan-400', to: 'to-cyan-600', shadow: 'shadow-cyan-500/20' },
  fun: { from: 'from-orange-400', to: 'to-orange-600', shadow: 'shadow-orange-500/20' },
  wellness: { from: 'from-red-400', to: 'to-red-600', shadow: 'shadow-red-500/20' }
};

// Definitive list of all tools
export const tools: Tool[] = [
  // Productivity Tools
  { path: '/task-board', icon: ListChecks, label: 'Task Board', category: 'productivity', description: 'Organize and track your tasks with our interactive Kanban board.' },
  { path: '/notes', icon: StickyNote, label: 'Notes', category: 'productivity', description: 'Create and organize your notes with custom colors and categories.' },

  // Calculation Tools
  { path: '/calculator', icon: Calc, label: 'Calculator', category: 'calculation', description: 'Perform quick calculations with our elegant calculator.' },
  { path: '/percentage-calculator', icon: Percent, label: 'Percentages', category: 'calculation', description: 'Calculate percentages, increases, and differences.' },
  { path: '/unit-converter', icon: Ruler, label: 'Unit Converter', category: 'calculation', description: 'Convert between multiple units of measurement.' },
  { path: '/number-base-converter', icon: ArrowRightLeft, label: 'Base Converter', category: 'calculation', description: 'Convert numbers between Binary, Decimal, and Hexadecimal systems.' },
  { path: '/loan-calculator', icon: Landmark, label: 'Loan Calculator', category: 'calculation', description: 'Estimate mortgage/loan payments, interest, and amortization.' },
  { path: '/tip-calculator', icon: HandCoins, label: 'Tip Calculator', category: 'calculation', description: 'Calculate tips and split bills easily among multiple people.' },
  { path: '/inflation-calculator', icon: TrendingUp, label: 'Inflation Calc', category: 'calculation', description: 'Estimate future value based on avg. inflation.' }, // Added Tool #36
  { path: '/sales-tax-calculator', icon: Receipt, label: 'Sales Tax Calc', category: 'calculation', description: 'Calculate sales tax amount and total price.' }, // Added Tool #66
  // Time Tools
  { path: '/date-difference', icon: Calendar, label: 'Date Difference', category: 'time', description: 'Calculate time intervals between dates.' },
  { path: '/age-calculator', icon: Clock, label: 'Age Calculator', category: 'time', description: 'Calculate exact ages from any birth date.' },
  { path: '/countdown-timer', icon: Timer, label: 'Countdown', category: 'time', description: 'Create precise countdowns to important dates.' },
  { path: '/stopwatch', icon: Watch, label: 'Stopwatch', category: 'time', description: 'High-precision stopwatch with lap timing.' },
  { path: '/workday-calculator', icon: Briefcase, label: 'Workdays', category: 'time', description: 'Calculate business days between dates.' },
  { path: '/timezone-converter', icon: Globe, label: 'Time Zones', category: 'time', description: 'Convert times across global time zones.' },
  { path: '/pomodoro-timer', icon: PomodoroIcon, label: 'Pomodoro Timer', category: 'time', description: 'Boost focus with the Pomodoro Technique timer.' },
  { path: '/unix-timestamp-converter', icon: CalendarClock, label: 'Timestamp Converter', category: 'time', description: 'Convert Unix timestamps to human-readable dates and vice-versa.' }, // Added category time

  // Document Tools
  { path: '/text-counter', icon: Type, label: 'Text Counter', category: 'document', description: 'Analyze text with detailed statistics.' },
  { path: '/text-compare', icon: FileText, label: 'Text Compare', category: 'document', description: 'Compare texts and find differences.' },
  { path: '/lorem-ipsum', icon: AlignLeft, label: 'Lorem Ipsum', category: 'document', description: 'Generate placeholder text for your designs.' },
  { path: '/pdf-converter', icon: FilePdf, label: 'PDF Converter', category: 'document', description: 'Convert various file formats to PDF.' },
  { path: '/json-validator', icon: FileJson, label: 'JSON Validator', category: 'document', description: 'Check if a given text string follows the correct JSON syntax rules.' },
  { path: '/remove-duplicate-lines', icon: ListMinus, label: 'Remove Duplicates', category: 'document', description: 'Remove duplicate lines from text or lists.' },
  { path: '/text-reverser', icon: Repeat, label: 'Text Reverser', category: 'document', description: 'Reverse the order of characters or words in any given text string.' },
  { path: '/text-sorting-tool', icon: SortAsc, label: 'Text Sorter', category: 'document', description: 'Sort lines of text alphabetically or numerically.' },
  { path: '/html-entity-converter', icon: Code, label: 'HTML Entity Converter', category: 'document', description: 'Encode/decode special characters to/from HTML entities.'}, // Added category document
  { path: '/markdown-editor', icon: FileCode, label: 'Markdown Editor', category: 'document', description: 'Write Markdown text and see the live HTML preview.' }, // Added Tool #61

  // Creative Tools
  { path: '/color-converter', icon: Palette, label: 'Color Converter', category: 'creative', description: 'Convert between color formats with precision.' },
  { path: '/qr-code-generator', icon: QrCode, label: 'QR Code Generator', category: 'creative', description: 'Generate QR codes for any content instantly.' },
  { path: '/background-remover', icon: Scissors, label: 'Background Remover', category: 'creative', description: 'Remove image backgrounds instantly.' },
  { path: '/meme-maker', icon: ImageIcon, label: 'Meme Maker', category: 'creative', description: 'Create and customize popular memes.' },
  { path: '/online-whiteboard', icon: Edit, label: 'Whiteboard', category: 'creative', description: 'A collaborative space for drawing, brainstorming, and mapping ideas.' },
  { path: '/color-picker', icon: Palette, label: 'Color Picker', category: 'creative', description: 'Select colors and get HEX, RGB, HSL values.' }, // Added Tool #28
  { path: '/css-gradient-generator', icon: Palette, label: 'CSS Gradient Gen', category: 'creative', description: 'Create linear & radial CSS gradients visually.' }, // Added Tool #29
  { path: '/placeholder-image-generator', icon: PlaceholderIcon, label: 'Placeholder Image', category: 'creative', description: 'Generate custom placeholder images via Canvas.' }, // Added Tool #63
  { path: '/ascii-art-generator', icon: Brush, label: 'ASCII Art Gen', category: 'creative', description: 'Convert text to ASCII art using various fonts.' }, // Added Tool #70
  { path: '/color-palette-generator', icon: Palette, label: 'Color Palette Gen', category: 'creative', description: 'Generate harmonious color palettes from a base color.' }, // Added Tool #74
  // Utility Tools
  { path: '/random-generator', icon: Dice, label: 'Random Numbers', category: 'utility', description: 'Generate random numbers with custom ranges.' },
  { path: '/password-generator', icon: Key, label: 'Password Generator', category: 'utility', description: 'Create secure, random passwords instantly.' },
  { path: '/weather', icon: Cloud, label: 'Weather', category: 'utility', description: 'Get real-time weather information.' },
  { path: '/base64-converter', icon: Binary, label: 'Base64 Converter', category: 'utility', description: 'Encode text to Base64 or decode Base64 back to text.' },
  { path: '/case-converter', icon: CaseSensitive, label: 'Case Converter', category: 'utility', description: 'Convert text between UPPERCASE, lowercase, Title Case, etc.' },
  { path: '/credit-card-validator', icon: CreditCard, label: 'Credit Card Validator', category: 'utility', description: 'Validate credit card number format using the Luhn algorithm.' },
  { path: '/fuel-cost-calculator', icon: Fuel, label: 'Fuel Cost Calculator', category: 'utility', description: 'Estimate fuel cost for a trip based on distance, efficiency, and price.' },
  { path: '/morse-code-translator', icon: Radio, label: 'Morse Code Translator', category: 'utility', description: 'Translate text to Morse code signals and decode Morse back to text.'},
  { path: '/random-name-generator', icon: Users, label: 'Random Names', category: 'utility', description: 'Generate random fictional first and last names.'},
  { path: '/url-encoder-decoder', icon: ArrowRightLeft, label: 'URL Encoder/Decoder', category: 'utility', description: 'Encode text to URL-safe format or decode encoded URLs.' }, // Added Tool #26
  { path: '/braille-translator', icon: Accessibility, label: 'Braille Translator', category: 'utility', description: 'Translate text into Grade 1 Braille characters.' }, // Added Tool #27
  { path: '/csv-json-converter', icon: ArrowRightLeft, label: 'CSV/JSON Converter', category: 'utility', description: 'Convert data between CSV and JSON formats.' }, // Added Tools #30 & #37
  { path: '/data-uri-generator', icon: LinkIcon, label: 'Data URI Generator', category: 'utility', description: 'Convert files into embeddable Data URIs.' }, // Added Tool #31
  { path: '/hashing-tool', icon: Hash, label: 'Hashing Tool', category: 'utility', description: 'Generate MD5, SHA1, SHA256, SHA512 hashes.' }, // Added Tool #32
  { path: '/htaccess-redirect-generator', icon: FileCode, label: '.htaccess Generator', category: 'utility', description: 'Generate common .htaccess redirect rules.' }, // Added Tool #33
  { path: '/html-table-generator', icon: Table, label: 'HTML Table Gen', category: 'utility', description: 'Generate HTML table markup from CSV data.' }, // Added Tool #34
  { path: '/iban-validator', icon: Landmark, label: 'IBAN Validator', category: 'utility', description: 'Validate IBAN format using checksum.' }, // Added Tool #35
  { path: '/password-strength-checker', icon: ShieldCheck, label: 'Password Strength', category: 'utility', description: 'Analyze password strength and get feedback.' }, // Added Tool #62
  { path: '/regex-tester', icon: FileCode, label: 'Regex Tester', category: 'utility', description: 'Test JavaScript regex patterns against text.' }, // Added Tool #64
  { path: '/robots-txt-generator', icon: FileCode, label: 'robots.txt Gen', category: 'utility', description: 'Generate robots.txt files with custom directives.' }, // Added Tool #65
  { path: '/subnet-calculator', icon: Network, label: 'Subnet Calculator', category: 'utility', description: 'Calculate IPv4 subnet details (network, broadcast, hosts).' }, // Added Tool #67
  { path: '/unicode-character-info', icon: Type, label: 'Unicode Info', category: 'utility', description: 'Get Unicode code point and name for a character.' }, // Added Tool #68
  { path: '/xml-validator', icon: FileCode, label: 'XML Validator', category: 'utility', description: 'Check if XML content is well-formed.' }, // Added Tool #69
  { path: '/code-formatter', icon: Wand2, label: 'Code Formatter', category: 'utility', description: 'Format code snippets using Prettier.' }, // Added Tool #72
  { path: '/code-minifier', icon: Zap, label: 'JS Minifier', category: 'utility', description: 'Minify JavaScript code using Terser.' }, // Added Tool #73 (Updated description)
  { path: '/css-validator', icon: ListChecks, label: 'CSS Validator', category: 'utility', description: 'Validate CSS code using the W3C service.' }, // Added Tool #75
  // Fun Tools
  { path: '/zodiac-finder', icon: Star, label: 'Zodiac Finder', category: 'fun', description: 'Discover your zodiac sign and characteristics.' },
  { path: '/random-fact', icon: Quote, label: 'Random Facts', category: 'fun', description: 'Discover interesting and probably useless facts.' },
  { path: '/buzzword-generator', icon: MessageSquare, label: 'Buzzword Generator', category: 'fun', description: 'Generate professional-sounding corporate buzzwords.' },
  { path: '/chuck-norris', icon: FaHatCowboy, label: 'Chuck Norris Jokes', category: 'fun', description: 'Generate hilarious Chuck Norris facts and jokes.' },
  { path: '/decision-maker-wheel', icon: Target, label: 'Decision Wheel', category: 'fun', description: 'Let fate decide! Make a random selection from a list of options.' },

  // Wellness
  { path: '/daily-affirmation', icon: Heart, label: 'Daily Affirmation', category: 'wellness', description: 'Get inspired with powerful daily affirmations.' },
  { path: '/bmi-calculator', icon: Scale, label: 'BMI Calculator', category: 'wellness', description: 'Calculate your Body Mass Index based on height and weight.' }
];

// Helper function to get category label (optional, but keeps consistency)
export const getCategoryLabel = (categoryKey: ToolCategoryKey): string => {
  const labels: Record<ToolCategoryKey, string> = {
    productivity: 'Productivity Tools',
    calculation: 'Calculation Tools',
    time: 'Time Tools',
    document: 'Document Tools',
    creative: 'Creative Tools',
    utility: 'Utility Tools',
    fun: 'Fun Tools',
    wellness: 'Wellness Tools'
  };
  return labels[categoryKey] || 'Unknown Category';
};